<template>
  <div class="element-label__actions" :class="{ 'element-label__actions__overflow-hidden': !displaySettingsIcon }">
    <BbBtn
      size="x-small"
      type="icon"
      icon="delete"
      icon-size="medium"
      color="white"
      no-background
      @click="$emit('remove')"
    />
    <BbBtn
      size="x-small"
      type="icon"
      icon="up"
      icon-size="medium"
      color="white"
      no-background
      @click="$emit('move-up')"
    />
    <BbBtn
      size="x-small"
      type="icon"
      icon="down"
      icon-size="medium"
      color="white"
      no-background
      @click="$emit('move-down')"
    />

    <div class="settings-container" v-if="displaySettingsIcon">
      <BbBtn
        size="x-small"
        type="icon"
        icon="settings"
        icon-size="medium"
        color="white"
        no-background
        @click="toggleDropdown"
      />
      <div v-if="dropdownOpen && options.length" class="dropdown-menu">
        <div
          v-for="option in options"
          :key="option.value"
          class="dropdown-item"
          @click="toggleValue(option.value, option.disabled)"
        >
          <BbCheckbox
            :model-value="val.includes(option.value)"
            class="select-list-item__checkbox filled-checkbox"
            :class="{ hidden: option.disabled }"
            size="small"
          />
          <BbIcon size="small" :name="option.icon" />
          <TextBody tag="span" :text="option.label" :body-type="1" weight="medium" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { useElementHighlight, useWesley } from '@anthill-modules/wesley-components';

import TextBody from '~/components/common/TextBody.vue';
import useDocumentEditor from '~/composables/useDocumentEditor';
import { CONFIG_ICON_COMPONENT } from '~/utils/icons';

export default {
  components: { TextBody },
  emits: ['remove', 'move-down', 'move-up'],
  props: {
    displayVisibilitySettings: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { selectedParent: selectedLayout } = useElementHighlight();
    const { asyncSendMessage: sendAssyncMessageToDocument, iframeWindow } = useWesley();
    const { updateEditorSelectableElementsData, renderCitationTooltipsInIframe, refreshReferencesOnUpdate } =
      useDocumentEditor();

    const val = ref([]);
    const dropdownOpen = ref(false);
    const options = ref([]);
    const displaySettingsIcon = ref(props.displayVisibilitySettings);

    const toggleDropdown = () => {
      dropdownOpen.value = !dropdownOpen.value;
    };

    const toggleValue = (value, disabled) => {
      if (disabled) {
        addBorderAnimation(value);
        return;
      }

      const index = val.value.indexOf(value);

      if (index === -1) {
        val.value.push(value);
      } else {
        val.value.splice(index, 1);
      }

      updateHiddenValue(value, index !== -1);
    };

    const updateHiddenValue = async (cssPath, hidden) => {
      // Waiting for response before continuing to avoid getting selectable elements
      // data that isn't yet updated, as it will result in flashing checkbox rendering
      await sendAssyncMessageToDocument({
        method: 'updateAttribute',
        params: {
          query: cssPath,
          attr: 'hidden',
          value: hidden,
        },
      });
      updateEditorSelectableElementsData();
      renderCitationTooltipsInIframe();

      setTimeout(() => {
        refreshReferencesOnUpdate();
      }, 2000);

      if (!hidden) {
        addBorderAnimation(cssPath);
      }
    };

    const addBorderAnimation = (cssPath) => {
      const iframeDoc = iframeWindow.value?.document;
      const element = iframeDoc?.querySelector(cssPath);

      if (element) {
        element.style.outline = '2px solid #4182ef';
        element.style.animation = 'pulse 1s ease-in-out';

        // Remove styles after the animation duration
        setTimeout(() => {
          element.style.outline = '';
          element.style.animation = '';
        }, 1000);
      } else {
        console.warn('Element not found for the given CSS path:', cssPath);
      }
    };

    const getIconByElementType = (element) =>
      element?.element?.tag ? CONFIG_ICON_COMPONENT.get(element.element.tag) : '';

    watch(
      [selectedLayout, dropdownOpen],
      ([newValueSelectedLayout, newDropdownOpen]) => {
        const newOptions = [];
        const newVal = [];

        if (!newValueSelectedLayout?.children?.length) {
          displaySettingsIcon.value = false;
        }

        if (newDropdownOpen && newValueSelectedLayout?.children?.length) {
          newValueSelectedLayout?.children.forEach((child) => {
            newOptions.push({
              value: child.cssPath,
              label: child.attributes?.fieldName,
              icon: getIconByElementType(child),
              disabled: child.attributes?.required,
            });

            if (!child.attributes?.hidden) {
              newVal.push(child.cssPath);
            }
          });
          displaySettingsIcon.value = true;
        }

        options.value = newOptions;
        val.value = newVal;
      },
      { immediate: true, deep: true },
    );

    return { options, val, toggleDropdown, toggleValue, dropdownOpen, displaySettingsIcon };
  },
};
</script>

<style lang="scss" scoped>
.element-label__actions {
  pointer-events: auto;
  display: flex;
  position: absolute;
  top: 6px;
  right: 6px;
  border-radius: 8px;
  border: 1px solid $bb-primary-3;

  &__overflow-hidden {
    overflow: hidden; //if this is always applied, settings dropdown will not be visible
  }

  button {
    background-color: $bb-neutral-0;
    border-radius: 0 !important;

    :deep(.bb-icon.medium) {
      color: $bb-primary-1;
      font-size: 16px !important;
    }

    &:first-of-type {
      background: $bb-interface-5 !important;
      border-bottom-left-radius: 7px !important;
      border-top-left-radius: 7px !important;

      :deep(.bb-icon.medium) {
        color: $bb-neutral-0;
      }
    }
  }

  .settings-container {
    :deep(.bb-btn.icon) {
      background: $bb-neutral-0 !important;
      border-radius: 0 7px 7px 0 !important;

      i {
        color: $bb-neutral-1 !important;
      }
    }

    .dropdown-menu {
      position: absolute;
      z-index: 1000;
      background: white;
      border: 1px solid #ccc;
      padding: 4px;
      margin-top: 5px;
      right: 0;

      .dropdown-item {
        padding: $bb-spacing-2 $bb-spacing-3;
        border-bottom: 1px solid #eee;
        cursor: pointer;
        display: flex !important;
        align-items: center;
        width: 100%;
        white-space: nowrap;
        gap: $bb-spacing-2;
        border-bottom: 1px solid #eee;

        .hidden {
          opacity: 0;
        }
      }
    }
  }
}
</style>
